import { Mail, Phone, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Category, Segment } from '@/data/mockData';

export interface VolumeTotals {
  email: number;
  mobile: number;
  emailOrMobile: number;
  emailAndMobile: number;
}

/** Volume agrégé d'une population (1 ligne = 1 population, pas de détail segment). */
export interface PopulationVolumeView {
  population: string;
  totals: VolumeTotals;
}

interface VolumeGridProps {
  selectedCrm: string;
  setSelectedCrm: (value: string) => void;
  availableCrmFiles: string[];
  selectedCrmExclusion: string[];
  setSelectedCrmExclusion: (files: string[]) => void;
  duration: '30' | '60' | '90';
  onDurationChange: (duration: '30' | '60' | '90') => void;
  selectedSegmentsByCategory: { category: Category; segments: Segment[] }[];
  estimateCalculated: boolean;
  estimatedTotals: VolumeTotals;
  selectedSegmentDetails: Segment[];
  isSingleSegmentInCategory: boolean;
  isEstimating: boolean;
  onEstimate: () => void;
  /** Si vrai (colonne `population` présente), affiche les onglets population/segment. */
  hasPopulation?: boolean;
  /** Vues par population (1 entrée par population détectée). */
  populationViews?: PopulationVolumeView[];
}

/** Grille des volumes par segment (vue "globale" ou "par population"). */
const SegmentVolumeTable = ({
  selectedSegmentsByCategory,
  duration,
  estimateCalculated,
}: {
  selectedSegmentsByCategory: { category: Category; segments: Segment[] }[];
  duration: '30' | '60' | '90';
  estimateCalculated: boolean;
}) => (
  <div className="space-y-4">
    {selectedSegmentsByCategory.map(({ category, segments }, categoryIndex) => (
      <div key={category.id}>
        <h4 className="mb-2 text-sm font-semibold text-primary">{category.name}</h4>
        <div className="overflow-hidden rounded-xl border border-white/20">
          {categoryIndex === 0 && (
            <div className="grid gap-2 bg-accent/50 px-3 py-2 text-xs font-medium text-muted-foreground lg:grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr]">
              <div>Segment</div>
              <div className="flex items-center justify-center gap-1">
                <Mail className="h-3 w-3" />
                Emails
              </div>
              <div className="flex items-center justify-center gap-1">
                <Phone className="h-3 w-3" />
                Mobiles
              </div>
              <div className="flex items-center justify-center gap-1">
                <Mail className="h-3 w-3" />
                <span className="text-[10px]">ou</span>
                <Phone className="h-3 w-3" />
              </div>
              <div className="flex items-center justify-center gap-1">
                <Mail className="h-3 w-3" />
                <span className="text-[10px]">et</span>
                <Phone className="h-3 w-3" />
              </div>
            </div>
          )}
          {segments.map((segment) => {
            const volumeData = segment.volumes?.[duration];
            return (
              <div key={segment.id} className="grid gap-2 px-3 py-2 text-sm lg:grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr]">
                <div className="truncate font-medium" title={segment.name}>
                  {segment.name}
                </div>
                <div className="text-center text-muted-foreground">
                  {estimateCalculated && volumeData?.email !== undefined ? volumeData.email.toLocaleString() : '—'}
                </div>
                <div className="text-center text-muted-foreground">
                  {estimateCalculated && volumeData?.mobile !== undefined ? volumeData.mobile.toLocaleString() : '—'}
                </div>
                <div className="text-center text-muted-foreground">
                  {estimateCalculated && volumeData?.emailOrMobile !== undefined ? volumeData.emailOrMobile.toLocaleString() : '—'}
                </div>
                <div className="text-center text-muted-foreground">
                  {estimateCalculated && volumeData?.emailAndMobile !== undefined ? volumeData.emailAndMobile.toLocaleString() : '—'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

/**
 * Grille des volumes par population : même format que la grille segment, mais
 * 1 ligne = 1 population (colonne 1 = nom de la population). Pas de détail segment.
 */
const PopulationVolumeTable = ({
  populationViews,
  estimateCalculated,
}: {
  populationViews: PopulationVolumeView[];
  estimateCalculated: boolean;
}) => (
  <div className="overflow-hidden rounded-xl border border-white/20">
    <div className="grid gap-2 bg-accent/50 px-3 py-2 text-xs font-medium text-muted-foreground lg:grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr]">
      <div>Population</div>
      <div className="flex items-center justify-center gap-1">
        <Mail className="h-3 w-3" />
        Emails
      </div>
      <div className="flex items-center justify-center gap-1">
        <Phone className="h-3 w-3" />
        Mobiles
      </div>
      <div className="flex items-center justify-center gap-1">
        <Mail className="h-3 w-3" />
        <span className="text-[10px]">ou</span>
        <Phone className="h-3 w-3" />
      </div>
      <div className="flex items-center justify-center gap-1">
        <Mail className="h-3 w-3" />
        <span className="text-[10px]">et</span>
        <Phone className="h-3 w-3" />
      </div>
    </div>
    {populationViews.map((view) => (
      <div
        key={view.population}
        className="grid gap-2 px-3 py-2 text-sm lg:grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr]"
      >
        <div className="truncate font-medium" title={view.population}>
          {view.population}
        </div>
        <div className="text-center text-muted-foreground">
          {estimateCalculated ? view.totals.email.toLocaleString() : '—'}
        </div>
        <div className="text-center text-muted-foreground">
          {estimateCalculated ? view.totals.mobile.toLocaleString() : '—'}
        </div>
        <div className="text-center text-muted-foreground">
          {estimateCalculated ? view.totals.emailOrMobile.toLocaleString() : '—'}
        </div>
        <div className="text-center text-muted-foreground">
          {estimateCalculated ? view.totals.emailAndMobile.toLocaleString() : '—'}
        </div>
      </div>
    ))}
  </div>
);

/** Bloc "Volume total" récapitulatif (4 lignes email/mobile/ou/et). */
const TotalsPanel = ({
  totals,
  estimateCalculated,
  title = 'Volume total:',
}: {
  totals: VolumeTotals;
  estimateCalculated: boolean;
  title?: string;
}) => (
  <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
    <h4 className="font-semibold text-sm mb-3 text-primary">{title}</h4>
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Emails</span>
        </div>
        <span className="font-bold text-success">
          {estimateCalculated ? totals.email.toLocaleString() : '—'}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Mobiles</span>
        </div>
        <span className="font-bold text-success">
          {estimateCalculated ? totals.mobile.toLocaleString() : '—'}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-primary" />
          <span className="text-sm font-medium">Emails</span>
          <span className="text-xs">ou</span>
          <Phone className="w-3.5 h-3.5 text-primary" />
          <span className="text-sm font-medium">Mobiles</span>
        </div>
        <span className="font-bold text-success">
          {estimateCalculated ? totals.emailOrMobile.toLocaleString() : '—'}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-primary" />
          <span className="text-sm font-medium">Emails</span>
          <span className="text-xs">et</span>
          <Phone className="w-3.5 h-3.5 text-primary" />
          <span className="text-sm font-medium">Mobiles</span>
        </div>
        <span className="font-bold text-success">
          {estimateCalculated ? totals.emailAndMobile.toLocaleString() : '—'}
        </span>
      </div>
    </div>
  </div>
);

export const VolumeGrid = ({
  selectedCrm,
  setSelectedCrm,
  availableCrmFiles,
  selectedCrmExclusion,
  setSelectedCrmExclusion,
  duration,
  onDurationChange,
  selectedSegmentsByCategory,
  estimateCalculated,
  estimatedTotals,
  selectedSegmentDetails,
  isSingleSegmentInCategory,
  isEstimating,
  onEstimate,
  hasPopulation = false,
  populationViews = [],
}: VolumeGridProps) => {
  const toggleExclusion = (fileName: string) => {
    if (selectedCrmExclusion.includes(fileName)) {
      setSelectedCrmExclusion(selectedCrmExclusion.filter((f) => f !== fileName));
    } else {
      setSelectedCrmExclusion([...selectedCrmExclusion, fileName]);
    }
  };

  const showPopulationTabs = hasPopulation && populationViews.length > 0;

  // Contenu de la vue "Par segment" (= comportement historique).
  const segmentView = (
    <>
      {isEstimating ? (
        <p className="text-sm text-muted-foreground">Estimation des volumes en cours...</p>
      ) : !selectedCrm ? (
        <p className="text-sm text-muted-foreground">Sélectionnez un CRM pour voir les volumes</p>
      ) : selectedSegmentsByCategory.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sélectionnez des segments pour voir les volumes</p>
      ) : (
        <SegmentVolumeTable
          selectedSegmentsByCategory={selectedSegmentsByCategory}
          duration={duration}
          estimateCalculated={estimateCalculated}
        />
      )}
    </>
  );

  return (
    <div className="w-3/5 flex flex-col overflow-hidden pl-4 border-l border-border min-h-0 min-w-0">
      {/* Sélection de CRM + CRM à exclure */}
      <div className="flex-shrink-0 mb-4 flex gap-4 pr-2">
        {/* Sélection de CRM */}
        <div className="flex-1 min-w-0">
          <h3 className="text-md font-semibold text-foreground mb-3 pt-6">Sélection de CRM</h3>
          <Select value={selectedCrm} onValueChange={setSelectedCrm} disabled={isEstimating}>
            <SelectTrigger className="w-full truncate">
              <SelectValue placeholder={availableCrmFiles.length ? 'Choisir un CRM...' : 'Aucun fichier CRM disponible'} />
            </SelectTrigger>
            <SelectContent>
              {availableCrmFiles.map((fileName) => {
                const label = fileName.toLowerCase().endsWith('.csv')
                  ? fileName.slice(0, -4)
                  : fileName;
                return (
                  <SelectItem key={fileName} value={fileName}>
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* CRM à exclure */}
        <div className="flex-1 min-w-0">
          <h3 className="text-md font-semibold text-foreground mb-3 pt-6">CRM à exclure</h3>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                disabled={isEstimating || availableCrmFiles.length === 0}
              >
                <span className="truncate">
                  {selectedCrmExclusion.length === 0
                    ? 'Aucun CRM exclu'
                    : `${selectedCrmExclusion.length} CRM exclu(s)`}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-2 w-[--radix-popover-trigger-width]" align="start">
              {availableCrmFiles.filter((f) => f !== selectedCrm).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-2">Aucun autre fichier CRM disponible</p>
              ) : (
                availableCrmFiles.filter((f) => f !== selectedCrm).map((fileName) => {
                  const label = fileName.toLowerCase().endsWith('.csv') ? fileName.slice(0, -4) : fileName;
                  const isChecked = selectedCrmExclusion.includes(fileName);
                  return (
                    <div
                      key={fileName}
                      className="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-accent select-none"
                      onClick={() => toggleExclusion(fileName)}
                    >
                      <div className="h-4 w-4 flex items-center justify-center shrink-0">
                        {isChecked && <X className="h-4 w-4 text-red-500" />}
                      </div>
                      <span className="text-sm truncate">{label}</span>
                    </div>
                  );
                })
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <h3 className="text-md font-semibold text-foreground mb-3">Durée des segments</h3>

      {/* Boutons durée - fixe */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex gap-2">
          <Button
            type="button"
            variant={duration === '30' ? 'default' : 'outline'}
            onClick={() => onDurationChange('30')}
            disabled={isEstimating}
            size="sm"
          >
            30j
          </Button>
          <Button
            type="button"
            variant={duration === '60' ? 'default' : 'outline'}
            onClick={() => onDurationChange('60')}
            disabled={isEstimating}
            size="sm"
          >
            60j
          </Button>
          <Button
            type="button"
            variant={duration === '90' ? 'default' : 'outline'}
            onClick={() => onDurationChange('90')}
            disabled={isEstimating}
            size="sm"
          >
            90j
          </Button>
        </div>
      </div>

      <h3 className="text-md font-semibold text-foreground mb-3">Volume</h3>

      {/* Volumes scrollables */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-background">
        {showPopulationTabs ? (
          <Tabs defaultValue="population" className="w-full">
            <TabsList className="mb-3">
              {/* "Par population" affiché en premier (cf. feature.md). */}
              <TabsTrigger value="population">Par population</TabsTrigger>
              <TabsTrigger value="segment">Par segment</TabsTrigger>
            </TabsList>

            <TabsContent value="population">
              {isEstimating ? (
                <p className="text-sm text-muted-foreground">Estimation des volumes en cours...</p>
              ) : !selectedCrm ? (
                <p className="text-sm text-muted-foreground">Sélectionnez un CRM pour voir les volumes</p>
              ) : selectedSegmentsByCategory.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sélectionnez des segments pour voir les volumes</p>
              ) : (
                <PopulationVolumeTable
                  populationViews={populationViews}
                  estimateCalculated={estimateCalculated}
                />
              )}
            </TabsContent>

            <TabsContent value="segment">{segmentView}</TabsContent>
          </Tabs>
        ) : (
          segmentView
        )}
      </div>

      {/* Estimation totale (globale) - Fixée en bas */}
      {selectedSegmentDetails.length > 0 && selectedCrm && (
        <div className="border-t pt-2 space-y-2 flex-shrink-0 mt-2">
          <TotalsPanel totals={estimatedTotals} estimateCalculated={estimateCalculated} />
          <Button
            onClick={onEstimate}
            disabled={isEstimating}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
          >
            {isEstimating ? 'Calcul en cours...' : 'Calculer les volumes'}
          </Button>
        </div>
      )}
    </div>
  );
};
